import React, { useState, useEffect } from "react";
import { apiCall } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Zap, Plus, Trash2, Edit3, Sparkles, ArrowRight, Users, MessageSquare } from "lucide-react";

const TRIGGERS = [
  { value: "client_created", label: "Client Created", desc: "Triggers when a new client registers or is imported" },
  { value: "call_completed", label: "Call Completed", desc: "Triggers after an agent completes a call" },
  { value: "deposit_detected", label: "Deposit Detected", desc: "Triggers when a client makes a deposit" },
  { value: "category_changed", label: "Category Changed", desc: "Triggers when client moves to Hot/Regular" },
  { value: "scheduled_cron", label: "Scheduled (Daily)", desc: "Triggers automatically every day at 9:00 AM" },
];

const ACTION_TYPES = [
  { value: "assign_agent", label: "Assign Agent", icon: Users },
  { value: "send_whatsapp", label: "Send WhatsApp Message", icon: MessageSquare },
  { value: "ai_generate_message", label: "AI Generate & Send Message", icon: Sparkles },
  { value: "update_category", label: "Update Client Category", icon: Edit3 },
  { value: "send_notification", label: "Notify Team", icon: Zap },
];

const TEMPLATES = [
  {
    name: "Auto-Assign New Clients",
    trigger: "client_created",
    conditions: [{ field: "category", operator: "equals", value: "new" }],
    actions: [{ type: "assign_agent", config: { mode: "round_robin" } }],
    badge: "Most Popular"
  },
  {
    name: "WhatsApp Welcome Message",
    trigger: "client_created",
    conditions: [],
    actions: [{ type: "send_whatsapp", config: { message: "Welcome to our platform! We're excited to have you here." } }],
    badge: "Instant Engagement"
  },
  {
    name: "AI Smart Lead Follow-up",
    trigger: "call_completed",
    conditions: [{ field: "response", operator: "equals", value: "interested" }],
    actions: [{ type: "ai_generate_message", config: { topic: "follow_up" } }],
    badge: "AI Powered"
  }
];

export default function AutomationBuilderPage() {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Form State
  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState("client_created");
  const [isActive, setIsActive] = useState(true);
  const [conditions, setConditions] = useState([]);
  const [actions, setActions] = useState([{ type: "assign_agent", config: { mode: "round_robin" } }]);
  const [saving, setSaving] = useState(false);
  const [testingAi, setTestingAi] = useState(false);
  const [aiSampleMsg, setAiSampleMsg] = useState("");

  const fetchAutomations = async () => {
    try {
      const { data } = await apiCall("get", "/automations");
      setAutomations(data.automations || []);
    } catch {
      toast.error("Failed to load automations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAutomations();
  }, []);

  const handleOpenCreate = () => {
    setEditingId(null);
    setName("");
    setTrigger("client_created");
    setIsActive(true);
    setConditions([]);
    setActions([{ type: "assign_agent", config: { mode: "round_robin" } }]);
    setDialogOpen(true);
  };

  const handleApplyTemplate = (tmpl) => {
    setEditingId(null);
    setName(tmpl.name);
    setTrigger(tmpl.trigger);
    setIsActive(true);
    setConditions(tmpl.conditions);
    setActions(tmpl.actions);
    setDialogOpen(true);
  };

  const handleEdit = (auto) => {
    setEditingId(auto.id || auto._id);
    setName(auto.name);
    setTrigger(auto.trigger);
    setIsActive(auto.is_active);
    setConditions(auto.conditions || []);
    setActions(auto.actions || []);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Automation name is required"); return; }
    setSaving(true);
    try {
      const payload = { name, trigger, conditions, actions, is_active: isActive };
      if (editingId) {
        await apiCall("put", `/automations/${editingId}`, payload);
        toast.success("Automation updated successfully");
      } else {
        await apiCall("post", "/automations", payload);
        toast.success("Automation created successfully");
      }
      setDialogOpen(false);
      fetchAutomations();
    } catch {
      toast.error("Failed to save automation");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this automation?")) return;
    try {
      await apiCall("delete", `/automations/${id}`);
      toast.success("Automation deleted");
      fetchAutomations();
    } catch {
      toast.error("Failed to delete automation");
    }
  };

  const handleToggle = async (id) => {
    try {
      const { data } = await apiCall("post", `/automations/${id}/toggle`);
      setAutomations(prev => prev.map(a => (a.id === id || a._id === id) ? { ...a, is_active: data.is_active } : a));
      toast.success(`Automation ${data.is_active ? "enabled" : "disabled"}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleTestAiMessage = async () => {
    setTestingAi(true);
    setAiSampleMsg("");
    try {
      const { data } = await apiCall("post", "/ai/generate-message", { topic: "welcome", prompt: "Write a high conversion welcome message for a new player." });
      setAiSampleMsg(data.message);
      toast.success(`AI Response generated (${data.source})`);
    } catch {
      toast.error("AI Generation failed");
    } finally {
      setTestingAi(false);
    }
  };

  const addConditionRow = () => setConditions([...conditions, { field: "category", operator: "equals", value: "new" }]);
  const removeConditionRow = (index) => setConditions(conditions.filter((_, i) => i !== index));
  const updateCondition = (index, key, val) => {
    const updated = [...conditions];
    updated[index][key] = val;
    setConditions(updated);
  };

  const addActionRow = () => setActions([...actions, { type: "send_whatsapp", config: { message: "" } }]);
  const removeActionRow = (index) => setActions(actions.filter((_, i) => i !== index));
  const updateActionType = (index, typeVal) => {
    const updated = [...actions];
    updated[index] = { type: typeVal, config: typeVal === "assign_agent" ? { mode: "round_robin" } : { message: "" } };
    setActions(updated);
  };
  const updateActionConfig = (index, key, val) => {
    const updated = [...actions];
    updated[index].config = { ...updated[index].config, [key]: val };
    setActions(updated);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="automation-builder-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Zap className="w-6 h-6 text-amber-500" /> Automation Builder
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Create automated CRM workflows to assign leads, send messages, and trigger AI actions</p>
        </div>
        <Button onClick={handleOpenCreate} className="flex items-center gap-2">
          <Plus className="w-4 h-4" /> Create Automation
        </Button>
      </div>

      {/* Templates Row */}
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Quick-Start Templates</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {TEMPLATES.map((tmpl, idx) => (
            <Card key={idx} className="border border-border hover:border-primary/50 transition-all cursor-pointer bg-card/60" onClick={() => handleApplyTemplate(tmpl)}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant="secondary" className="text-[10px]">{tmpl.badge}</Badge>
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                </div>
                <h4 className="font-semibold text-sm">{tmpl.name}</h4>
                <p className="text-xs text-muted-foreground">Trigger: <span className="font-mono text-foreground">{tmpl.trigger}</span></p>
                <div className="pt-2 flex items-center text-xs text-primary font-medium">
                  Use Template <ArrowRight className="w-3 h-3 ml-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Active Automations List */}
      <Card className="border border-border">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base font-semibold">Configured Automations</CardTitle>
            <CardDescription className="text-xs">Active workflows currently evaluating events in your CRM</CardDescription>
          </div>
          <Badge variant="outline" className="text-xs font-normal">
            {automations.length} {automations.length === 1 ? "workflow" : "workflows"}
          </Badge>
        </CardHeader>
        <CardContent className="p-0">
          {automations.length === 0 ? (
            <div className="p-8 text-center space-y-3">
              <Zap className="w-10 h-10 mx-auto text-muted-foreground/40" />
              <p className="text-sm font-medium text-muted-foreground">No automations configured yet</p>
              <Button variant="outline" size="sm" onClick={handleOpenCreate}>
                <Plus className="w-4 h-4 mr-1" /> Create Your First Workflow
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase">Workflow Name</TableHead>
                  <TableHead className="text-xs uppercase">Trigger Event</TableHead>
                  <TableHead className="text-xs uppercase">Actions Count</TableHead>
                  <TableHead className="text-xs uppercase">Status</TableHead>
                  <TableHead className="text-xs uppercase text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {automations.map((auto) => (
                  <TableRow key={auto.id || auto._id}>
                    <TableCell>
                      <p className="font-medium text-sm">{auto.name}</p>
                      <p className="text-[10px] text-muted-foreground">Created {auto.created_at ? new Date(auto.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : "recently"}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs font-mono">
                        {auto.trigger}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {auto.actions?.length || 0} step(s)
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={auto.is_active}
                          onCheckedChange={() => handleToggle(auto.id || auto._id)}
                        />
                        <span className={`text-xs ${auto.is_active ? "text-green-600 font-medium" : "text-muted-foreground"}`}>
                          {auto.is_active ? "Active" : "Disabled"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(auto)} className="h-8 w-8 p-0">
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(auto.id || auto._id)} className="h-8 w-8 p-0 text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Workflow Builder Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              {editingId ? "Edit Automation Workflow" : "Create Automation Workflow"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Name & Active Toggle */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Workflow Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Auto-Assign New Leads & Send Welcome WA"
                  className="mt-1 text-sm"
                />
              </div>
              <div className="flex items-center gap-3 p-2.5 rounded-md bg-muted/50 border border-border">
                <Switch checked={isActive} onCheckedChange={setIsActive} id="dialog-active-switch" />
                <Label htmlFor="dialog-active-switch" className="text-xs font-medium cursor-pointer">
                  {isActive ? "Active Workflow" : "Disabled"}
                </Label>
              </div>
            </div>

            {/* Trigger Selection */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">1. When this event happens (Trigger)</Label>
              <Select value={trigger} onValueChange={setTrigger}>
                <SelectTrigger className="h-10 text-sm">
                  <SelectValue placeholder="Select Trigger Event" />
                </SelectTrigger>
                <SelectContent>
                  {TRIGGERS.map((tr) => (
                    <SelectItem key={tr.value} value={tr.value}>
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">{tr.label}</span>
                        <span className="text-[10px] text-muted-foreground">{tr.desc}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Conditions Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">2. Filter Conditions (Optional)</Label>
                <Button variant="ghost" size="sm" onClick={addConditionRow} className="h-7 text-xs text-primary">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Filter Rule
                </Button>
              </div>
              {conditions.length === 0 ? (
                <p className="text-xs text-muted-foreground italic bg-muted/30 p-2.5 rounded-md">
                  No conditions set — workflow will trigger for all events of this type.
                </p>
              ) : (
                <div className="space-y-2">
                  {conditions.map((cond, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-md bg-muted/40 border border-border">
                      <Select value={cond.field} onValueChange={(v) => updateCondition(idx, "field", v)}>
                        <SelectTrigger className="h-8 text-xs w-36"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="category">Category</SelectItem>
                          <SelectItem value="site">Site</SelectItem>
                          <SelectItem value="response">Call Response</SelectItem>
                          <SelectItem value="source">Source</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={cond.operator} onValueChange={(v) => updateCondition(idx, "operator", v)}>
                        <SelectTrigger className="h-8 text-xs w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="equals">Equals</SelectItem>
                          <SelectItem value="not_equals">Not Equals</SelectItem>
                          <SelectItem value="contains">Contains</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        value={cond.value}
                        onChange={(e) => updateCondition(idx, "value", e.target.value)}
                        placeholder="Value (e.g. new, hot)"
                        className="h-8 text-xs flex-1"
                      />
                      <Button variant="ghost" size="sm" onClick={() => removeConditionRow(idx)} className="h-8 w-8 p-0 text-destructive">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">3. Perform Actions (Then do this)</Label>
                <Button variant="ghost" size="sm" onClick={addActionRow} className="h-7 text-xs text-primary">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Action
                </Button>
              </div>
              <div className="space-y-3">
                {actions.map((act, idx) => (
                  <Card key={idx} className="border border-border">
                    <CardContent className="p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">Step {idx + 1}</Badge>
                          <Select value={act.type} onValueChange={(v) => updateActionType(idx, v)}>
                            <SelectTrigger className="h-8 text-xs w-56 font-medium"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ACTION_TYPES.map(at => (
                                <SelectItem key={at.value} value={at.value}>
                                  <div className="flex items-center gap-1.5 text-xs">
                                    <at.icon className="w-3.5 h-3.5 text-primary" /> {at.label}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {actions.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeActionRow(idx)} className="h-7 w-7 p-0 text-destructive">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>

                      {/* Config Options per Action Type */}
                      {act.type === "assign_agent" && (
                        <div className="p-2.5 rounded-md bg-muted/40 text-xs flex items-center gap-3">
                          <Label className="text-xs whitespace-nowrap">Assignment Mode:</Label>
                          <Select
                            value={act.config?.mode || "round_robin"}
                            onValueChange={(v) => updateActionConfig(idx, "mode", v)}
                          >
                            <SelectTrigger className="h-7 text-xs w-48"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="round_robin">Round Robin (Distribute evenly)</SelectItem>
                              <SelectItem value="active_only">Active Agents Only</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {act.type === "send_whatsapp" && (
                        <div className="space-y-1">
                          <Label className="text-[10px] uppercase text-muted-foreground">Message Template / Content</Label>
                          <Input
                            value={act.config?.message || ""}
                            onChange={(e) => updateActionConfig(idx, "message", e.target.value)}
                            placeholder="e.g. Hello {username}, welcome to our service!"
                            className="h-8 text-xs font-mono"
                          />
                        </div>
                      )}

                      {act.type === "ai_generate_message" && (
                        <div className="space-y-2 p-2.5 rounded-md bg-purple-500/5 border border-purple-500/20">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 flex items-center gap-1">
                              <Sparkles className="w-3.5 h-3.5" /> OpenAI Smart Prompt Generator
                            </span>
                            <Button size="sm" variant="outline" onClick={handleTestAiMessage} disabled={testingAi} className="h-6 text-[10px] px-2">
                              {testingAi ? "Generating..." : "Test AI Output"}
                            </Button>
                          </div>
                          <Select
                            value={act.config?.topic || "follow_up"}
                            onValueChange={(v) => updateActionConfig(idx, "topic", v)}
                          >
                            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="welcome">Welcome Message</SelectItem>
                              <SelectItem value="follow_up">Call Follow-Up Reminder</SelectItem>
                              <SelectItem value="hot_lead">Hot Lead Bonus Offer</SelectItem>
                              <SelectItem value="reengagement">Re-engagement Pitch</SelectItem>
                            </SelectContent>
                          </Select>
                          {aiSampleMsg && (
                            <div className="p-2 rounded bg-background border border-purple-500/30 text-xs font-mono text-purple-900 dark:text-purple-200">
                              "{aiSampleMsg}"
                            </div>
                          )}
                        </div>
                      )}

                      {act.type === "update_category" && (
                        <div className="p-2.5 rounded-md bg-muted/40 text-xs flex items-center gap-3">
                          <Label className="text-xs whitespace-nowrap">Target Category:</Label>
                          <Select
                            value={act.config?.category || "hot"}
                            onValueChange={(v) => updateActionConfig(idx, "category", v)}
                          >
                            <SelectTrigger className="h-7 text-xs w-36"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="hot">Hot</SelectItem>
                              <SelectItem value="regular">Regular</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="pt-3 border-t border-border">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update Workflow" : "Create Workflow"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
